import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { IntlShape, useIntl } from 'react-intl';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import React, { ReactElement, ReactNode } from 'react';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { LabelledInputTextarea } from '../../../components/form/LabelledInputTextarea';
import {
  UrlDTO,
  WorkLanguageDTO,
  WorkOtherTitleDTO,
  WorkPlaceDTO,
  WorkPublisherDTO,
  WorkTitleAbbreviationDTO,
} from '../../../../api/core';
import { WorkLanguages } from '../../components/parts/WorkLanguages';
import { WorkOtherTitles } from '../../components/parts/WorkOtherTitles';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { WorkIssued } from '../../components/parts/WorkIssued';
import { WorkDateTypes } from '../../components/parts/WorkDateTypes';
import { Urls } from '../../components/parts/Urls';
import { WorkPublishers } from '../../components/parts/WorkPublishers';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { InputGroup } from '../../components/layout/InputGroup';
import { Panel } from '../../components/layout/Panel';
import { getDefaultWorkPlace } from '../../../../Settings/emptyModels';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { LabelledChips } from '../../../components/form/LabelledChips';

export const BasicPage = (props: Props): ReactElement => {
  const intl: IntlShape = useIntl();
  return (
    <Block title={`work.header.basic`}>
      <PermissionGate always={false} entity={props.permissionEntity} field={'form_subject'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-6'}>
            <LabelledDropdown
              id={'formSubject'}
              autoFocus={true}
              value={props.modifiedModel.formSubject}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.form_subject}
              onChange={(formSubject: string): void => props.setField('formSubject', formSubject)}
              title={'work.attr.formSubject'}
              options={getVocabularyOptions(intl.locale, 'form_subject_list')}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'resource_type'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-6'}>
            <LabelledDropdown
              id={'resourceType'}
              value={props.modifiedModel.resourceType}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.resource_type}
              onChange={(resourceType: string): void => props.setField('resourceType', resourceType)}
              title={'work.attr.resourceType'}
              options={getVocabularyOptions(intl.locale, 'resource_type_list', props.pubtypeGroup)}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'publication_status'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-6'}>
            <LabelledDropdown
              id={'publicationStatus'}
              value={props.modifiedModel.publicationStatus}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.publication_status}
              onChange={(publicationStatus: string): void => props.setField('publicationStatus', publicationStatus)}
              title={'work.attr.publicationStatus'}
              options={getVocabularyOptions(intl.locale, 'publication_status_list', props.pubtypeGroup)}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'version'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-6'}>
            <LabelledDropdown
              id={'version'}
              value={props.modifiedModel.version}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.version}
              onChange={(version: string): void => props.setField('version', version)}
              title={'work.attr.version'}
              options={getVocabularyOptions(intl.locale, 'version_list', props.pubtypeGroup)}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'title'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputTextarea
              id={'title'}
              value={props.modifiedModel.title}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.title}
              onChange={(title: string): void => props.setField('title', title)}
              title={'work.attr.title'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'subtitle'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputTextarea
              id={'subtitle'}
              value={props.modifiedModel.subtitle}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.subtitle}
              onChange={(subtitle: string): void => props.setField('subtitle', subtitle)}
              title={'work.attr.subtitle'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'work_languages'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <WorkLanguages
              autoFocus={props.autoFocus}
              checkPermission={checkPermission}
              listPartition={props.pubtypeGroup}
              workLanguages={props.modifiedModel.workLanguages ? props.modifiedModel.workLanguages : []}
              onChange={(wls: WorkLanguageDTO[]): void => props.setField('workLanguages', wls)}
              validation={props.validation?.root?.fields?.work_languages}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'title_supplement'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputTextarea
              id={'title_supplement'}
              value={props.modifiedModel.titleSupplement || ''}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.title_supplement}
              onChange={(titleSupplement: string): void => props.setField('titleSupplement', titleSupplement)}
              title={'work.attr.titleSupplement'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'work_other_titles'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <WorkOtherTitles
              autoFocus={props.autoFocus}
              listPartition={props.pubtypeGroup}
              workOtherTitles={props.modifiedModel.workOtherTitles || []}
              onChange={(data: WorkOtherTitleDTO[]): void => props.setField('workOtherTitles', data)}
              checkPermission={checkPermission}
              validation={props.validation?.root?.fields?.work_other_titles}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'subseries_title'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputText
              onChange={(data: string): void => props.setField('subseriesTitle', data)}
              id={'subseriesTitle'}
              value={props.modifiedModel.subseriesTitle}
              title={'work.attr.subseriesTitle'}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.subseries_title}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'work_title_abbreviations'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledChips
              onChange={(data: string[]): void => {
                const abbr: WorkTitleAbbreviationDTO[] = data.map((chip: string): WorkTitleAbbreviationDTO => {
                  const split: string[] = chip.split('#');
                  return {
                    abbreviation: split.pop(),
                    id: split.length > 0 ? parseInt(split[0], 10) : undefined,
                  };
                });
                props.setField('workTitleAbbreviations', abbr);
              }}
              id={'titleAbbreviations'}
              value={(props.modifiedModel.workTitleAbbreviations || []).map(
                (abbr: WorkTitleAbbreviationDTO): string => `${abbr.id ? `${abbr.id}#` : ''}${abbr.abbreviation}`
              )}
              title={'work.attr.titleAbbreviations'}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.work_title_abbreviations}
              itemTemplate={(value: string): ReactNode => value.split('#').pop()}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'edition'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputText
              id={'edition'}
              value={props.modifiedModel.edition}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.edition}
              onChange={(edition: string): void => props.setField('edition', edition)}
              title={'work.attr.edition'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'number_of_volumes'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputText
              id={'numberOfVolumes'}
              value={props.modifiedModel.numberOfVolumes}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.number_of_volumes}
              onChange={(numberOfVolumes: string): void => props.setField('numberOfVolumes', numberOfVolumes)}
              title={'work.attr.numberOfVolumes'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'issued'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <WorkIssued
              id={'issued'}
              mask={'9999?9999'}
              placeholder={'YYYY / YYYYMMDD'}
              value={props.modifiedModel.issued?.toString(10) || ''}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.issued}
              onChange={(issued: string): void => props.setField('issued', issued)}
              label={'work.attr.issued'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'work_date_types'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <WorkDateTypes
              autoFocus={props.autoFocus}
              title={'work.attr.workDateTypes'}
              dates={props.modifiedModel.workDateTypes}
              onChange={(dates: WorkOtherTitleDTO[]): void => props.setField('workDateTypes', dates)}
              pubtypeGroup={props.pubtypeGroup}
              validation={props.validation?.root?.fields?.work_date_types}
              checkPermission={checkPermission}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'work_places'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <Panel title={'work.attr.workPlaces'} toggleable={true}>
              <MultiInputControls
                canEdit={checkPermission().canWrite}
                lines={props.modifiedModel.workPlaces}
                addNew={(rows: WorkPlaceDTO[]): number => rows.push(getDefaultWorkPlace())}
                onChange={(workPlaces: WorkPlaceDTO[]): void => props.setField('workPlaces', workPlaces)}
                renderLines={(
                  workPlace: WorkPlaceDTO,
                  onChange: (dto: WorkPlaceDTO) => void,
                  actions: Actions,
                  idx: number
                ): ReactNode => (
                  <InputGroup delete={{ disabled: !checkPermission().canWrite, action: actions.delete }}>
                    <LabelledInputText
                      autoFocus={props.autoFocus}
                      id={`workPlace-${idx}`}
                      value={workPlace.place}
                      onChange={(place: string): void => onChange({ ...workPlace, place })}
                      permissions={checkPermission('place')}
                      validation={props.validation?.root?.fields.work_places?.children[idx].fields.place}
                    />
                  </InputGroup>
                )}
              />
            </Panel>
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'number_of_pages'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputText
              id={'number_of_pages'}
              value={props.modifiedModel.numberOfPages}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.number_of_pages}
              onChange={(numberOfPages: string): void => props.setField('numberOfPages', numberOfPages)}
              title={'work.attr.numberOfPages'}
              placeholder={intl.formatMessage({ id: 'work.attr.numberOfPages.placeholder' })}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'number'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputText
              id={'number'}
              value={props.modifiedModel.number}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.number}
              onChange={(number: string): void => props.setField('number', number)}
              title={'work.attr.number'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'work_publishers'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <div>
              <WorkPublishers
                autoFocus={props.autoFocus}
                publishers={props.modifiedModel.workPublishers || []}
                onChange={(workPublishers: WorkPublisherDTO[]): void => props.setField('workPublishers', workPublishers)}
                checkPermission={checkPermission}
                validation={props.validation?.root?.fields?.work_publishers}
                typelist={''}
              />
            </div>
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'size'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputText
              id={'size'}
              value={props.modifiedModel.size}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.size}
              onChange={(size: string): void => props.setField('size', size)}
              title={'work.attr.size'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'urls'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <Urls
              autoFocus={props.autoFocus}
              title={'work.attr.urls'}
              urls={props.modifiedModel.urls || []}
              onChange={(urls: UrlDTO[]): void => props.setField('urls', urls)}
              checkPermission={checkPermission}
              validation={props.validation?.root?.fields?.urls}
              typelist={'url_list_work'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'format'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-6'}>
            <LabelledDropdown
              value={props.modifiedModel.format || null}
              onChange={(format: string): void => props.setField('format', format)}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.format}
              options={getVocabularyOptions(intl.locale, 'format_list', props.pubtypeGroup)}
              title={'work.attr.format'}
              id={'format'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={'work_generic'} field={'medium'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-6'}>
            <LabelledDropdown
              value={props.modifiedModel.medium || null}
              onChange={(medium: string): void => props.setField('medium', medium)}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.medium}
              options={getVocabularyOptions(intl.locale, 'medium_list', props.pubtypeGroup)}
              title={'work.attr.medium'}
              id={'medium'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity="work_generic" field="note">
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledInputTextarea
              id={'note'}
              value={props.modifiedModel.note || ''}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.note}
              onChange={(note: string): void => props.setField('note', note.length > 0 ? note : null)}
              autoResize={true}
              rows={3}
              title={'work.attr.note'}
            />
          </div>
        )}
      </PermissionGate>
      <PermissionGate always={false} entity={props.permissionEntity} field={'peer_reviewed'}>
        {(checkPermission: CheckPermissionFunction): ReactElement => (
          <div className={'col-12'}>
            <LabelledDropdown
              value={props.modifiedModel.peerReviewed || 'no_value'}
              onChange={(peerReviewed: string): void => props.setField('peerReviewed', peerReviewed)}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.peer_reviewed}
              options={getVocabularyOptions(intl.locale, 'peer_review_status_list', props.pubtypeGroup)}
              title={'work.attr.peerReviewed'}
              id={'props.permissionEntity'}
            />
          </div>
        )}
      </PermissionGate>
    </Block>
  );
};
